import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { setCartCount } from '@/components/properties/property-view/slice/OrderCartSlice';
import {
  useGetCartQuery,
  useCreateCartFromAssetMutation,
  useUpdateCartMutation,
  useDeleteAssetOrderMutation,
} from '@/components/orders/services/orderApi';
import { applyAssetGroupEdit, stripLocalFields } from '@/components/orders/cart/utils/cartTransforms';
import type { ImageAssetType, ServiceType } from '@/components/ui/carousel';
import { getServiceUnitPrice } from '@/util/service-pricing';
import { useServiceQuote } from './useServiceQuote';
import { buildAssetCartItem } from './orderServicePayloads';
import { AI_VIRTUAL_STAGING_SLUG, AI_VIRTUAL_STAGING_NOTE } from './constants';

type EditContext = {
  lineItemUUID?: string;   // stable cart-line id (DV-2131 §3); preferred over the composite below
  lineAssetId: string;     // legacy fallback: stable cart-line assetId (NOT assetRefs[0])
  initialNote: string;
  originalRefIds: string[]; // all photo assetIds originally on the line
};

type Params = {
  photos: ImageAssetType[];
  service: ServiceType;
  propertyAddress: string;
  onClose: () => void;
  edit?: EditContext;
};

export function useAssetServiceOrder({ photos, service, propertyAddress, onClose, edit }: Params) {
  const dispatch = useDispatch();
  const userId = localStorage.getItem('userId') ?? '';
  const isAiStaging = service.shoppingCartSlug === AI_VIRTUAL_STAGING_SLUG;

  const { data: cart } = useGetCartQuery(userId, { skip: !userId });
  const [createCartFromAsset] = useCreateCartFromAssetMutation();
  const [updateCart] = useUpdateCartMutation();
  const [deleteAssetOrder] = useDeleteAssetOrderMutation();

  const isEdit = !!edit;
  const slug = service.shoppingCartSlug;

  const [items, setItems] = useState<ImageAssetType[]>(photos);
  const [note, setNote] = useState(
    edit ? edit.initialNote : isAiStaging ? AI_VIRTUAL_STAGING_NOTE : ''
  );
  const [submitting, setSubmitting] = useState(false);

  const count = items.length;
  const { subtotal: quotedSubtotal } = useServiceQuote(service.shoppingCartSlug, count);
  const clientUnitPrice = getServiceUnitPrice(service);
  const subtotal = quotedSubtotal ?? clientUnitPrice * count;
  const unitPrice = count > 0 ? subtotal / count : clientUnitPrice;
  const isFree = subtotal === 0;

  // AC7: description required for all services except AI Virtual Staging.
  const canSubmit = count > 0 && (isAiStaging || note.trim().length > 0);

  const removePhoto = (assetId: string) => {
    if (isEdit && items.length === 1 && items[0]?.assetUUID === assetId) {
      void removeLine();
      onClose();
      return;
    }
    setItems((prev) => {
      const next = prev.filter((p) => p.assetUUID !== assetId);
      if (next.length === 0) onClose();
      return next;
    });
  };

  const addToCart = async () => {
    setSubmitting(true);
    try {
      const editPhotoOrders = [buildAssetCartItem(items, service, note, propertyAddress)];
      const response = await createCartFromAsset({
        cartId: cart?.cartId ?? null,
        body: { userId, editPhotoOrders },
      }).unwrap();
      dispatch(showAlert({ type: Variants.SUCCESS, message: 'Added to cart successfully' }));
      const editLen = response?.editPhotoOrders?.length ?? 0;
      const itemLen = response?.items?.length ?? 0;
      dispatch(setCartCount(editLen + itemLen));
      return true;
    } catch (error) {
      console.error('Error adding asset service to cart:', error);
      dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to add to cart' }));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const updateLine = async (): Promise<boolean> => {
    if (!edit) return false;
    setSubmitting(true);
    try {
      const currentIds = new Set(items.map((p) => p.assetUUID));
      const removedRefIds = edit.originalRefIds.filter((id) => !currentIds.has(id));
      const updated = applyAssetGroupEdit(cart?.editPhotoOrders ?? [], {
        lineItemUUID: edit.lineItemUUID,
        lineAssetId: edit.lineAssetId,
        lineSlug: slug,
        removedRefIds,
        note,
      });
      const res = await updateCart({
        cartId: cart?.cartId,
        userId,
        items: (cart?.items ?? []).map(stripLocalFields),
        editPhotoOrders: updated,
      }).unwrap();
      dispatch(showAlert({ type: Variants.SUCCESS, message: 'Cart updated successfully' }));
      const itemLen = res?.items?.length ?? 0;
      const editLen = res?.editPhotoOrders?.length ?? 0;
      dispatch(setCartCount(itemLen + editLen));
      return true;
    } catch (error) {
      console.error('Error updating asset group:', error);
      dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to update cart' }));
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Deletes the whole grouped line by its stable lineItemUUID (DV-2131 §3), falling back to the
  // (assetId, slug) composite for a legacy line. `silent` suppresses the success toast for the
  // post-order cleanup path (the order confirmation already tells the user what happened),
  // matching the best-effort cleanup PaymentPage performs after checkout.
  const removeLine = async ({ silent = false }: { silent?: boolean } = {}): Promise<boolean> => {
    if (!edit) return false;
    try {
      await deleteAssetOrder({
        userId,
        lineItemUUID: edit.lineItemUUID,
        assetId: edit.lineAssetId,
        slug,
      }).unwrap();
      if (!silent) {
        dispatch(showAlert({ type: Variants.SUCCESS, message: 'Item removed from cart successfully' }));
      }
      return true;
    } catch (error) {
      console.error('Error removing asset line from cart:', error);
      if (!silent) {
        dispatch(showAlert({ type: Variants.ERROR, message: 'Failed to remove item from cart' }));
      }
      return false;
    }
  };

  const submit = () => (isEdit ? updateLine() : addToCart());
  const submitLabel = isEdit ? 'Update Cart' : 'Add to Cart';

  // Plain object (not useMemo): the returned closures are recreated each render
  // anyway, so memoizing buys no stability and a truncated dep list would only
  // risk stale closures. Mirrors use-asset-cart.ts.
  return { items, note, setNote, removePhoto, canSubmit, submitting, addToCart, submit, submitLabel, isEdit, removeLine, count, unitPrice, subtotal, isFree, isAiStaging };
}
